import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import OneSignal from 'react-onesignal';
import { toast } from 'sonner';
import { applyAppBadge, enableWebPushBadge, disableWebPushBadge, sendTestWebPush, isWebPushSupported, isBadgingSupported } from '../utils/webPush';

const ONESIGNAL_APP_ID = '8f2a295c-c2f2-492e-8c66-3db72496c1d0';
const API = process.env.REACT_APP_BACKEND_URL;

const NotificationContext = createContext(null);

export const useNotifications = () => useContext(NotificationContext);

// Module-level guard prevents React StrictMode double-init
let _initStarted = false;
let _sdkReady = false;

// Dedupe automatic (non-user-initiated) /subscribe POSTs. React StrictMode
// double-invokes effects in dev and the init + subscriptionId effects can both
// fire on load; without this they'd send identical requests that count against
// the anti-abuse rate limiter. User-initiated subscribes (enable/ensure) are
// intentional and NOT routed through here.
let _lastAutoSub = { key: '', ts: 0 };
const _postSubscribeDeduped = (api, headers, payload) => {
  try {
    const key = JSON.stringify(payload);
    const now = Date.now();
    if (key === _lastAutoSub.key && now - _lastAutoSub.ts < 10000) return;
    _lastAutoSub = { key, ts: now };
    fetch(`${api}/api/notifications/subscribe`, { method: 'POST', headers, body: key }).catch(() => {});
  } catch (_) { /* noop */ }
};

// Generate or retrieve a local subscription ID for email-only users
const getLocalSubId = () => {
  let id = localStorage.getItem('find2day_sub_id');
  if (!id) {
    id = 'local-' + crypto.randomUUID();
    localStorage.setItem('find2day_sub_id', id);
  }
  return id;
};

// Feb 2026 hardening — OneSignal player ids are canonical UUIDs. Never persist
// any other shape (empty string, our internal `local-…` fallback, or a
// truncated legacy id) as `onesignal_sub_id`; the backend rejects it on push
// dispatch and the whole batch fails.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isValidOneSignalId = (v) => typeof v === 'string' && UUID_RE.test(v.trim());
const readValidOneSignalId = () => {
  try {
    const raw = localStorage.getItem('onesignal_sub_id') || '';
    return isValidOneSignalId(raw) ? raw.trim() : '';
  } catch (_) { return ''; }
};

export const NotificationProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [sdkAvailable, setSdkAvailable] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(() => getLocalSubId());
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [initError, setInitError] = useState('');
  const [preferences, setPreferences] = useState({
    favouriteBusinessIds: [],
    followedBusinessIds: [],
    areaSubscriptions: [],
    notificationsEnabled: false,
    email: '',
    emailEnabled: false,
    featuredContentEnabled: false,
  });

  // Initialize OneSignal
  useEffect(() => {
    if (_initStarted) return;
    _initStarted = true;

    const init = async () => {
      try {
        await OneSignal.init({
          appId: ONESIGNAL_APP_ID,
          allowLocalhostAsSecureOrigin: true,
          serviceWorkerParam: { scope: '/onesignal/' },
          serviceWorkerPath: '/onesignal/OneSignalSDKWorker.js',
        });

        _sdkReady = true;
        setSdkAvailable(true);

        const permission = OneSignal.Notifications.permission;
        if (permission) {
          const osId = OneSignal.User.PushSubscription.id;
          if (osId && isValidOneSignalId(osId)) {
            // Iter 207 — keep the CANONICAL device id (find2day_sub_id) as the
            // single subscription key. The OneSignal player id is stored as a
            // field on that doc, never as a second key. Feb 2026 — only store
            // the id when it's a real UUID; anything else (empty, malformed)
            // stays out of localStorage and out of the backend row.
            localStorage.setItem('onesignal_sub_id', osId);
            setIsSubscribed(true);
            _postSubscribeDeduped(API, _authHeaders(), { subscriptionId: getLocalSubId(), hasPush: true, oneSignalId: osId });
          }
        }
      } catch (e) {
        const msg = e?.message || String(e);
        if (msg.includes('already initialized')) {
          _sdkReady = true;
          setSdkAvailable(true);
        } else {
          setInitError(msg);
          // Check if user already has a local (email-only) subscription
          const localId = localStorage.getItem('find2day_sub_id');
          if (localId) {
            setSubscriptionId(localId);
            setIsSubscribed(true);
          }
        }
      }
      setInitialized(true);
    };

    init();
  }, []);

  // Load preferences from backend when subscriptionId is known
  const loadPrefs = useCallback(async () => {
    if (!subscriptionId) return;
    try {
      const res = await fetch(`${API}/api/notifications/preferences/${subscriptionId}`);
      if (res.ok) {
        const data = await res.json();
        // Iter 211 — for logged-in users the authoritative email opt-in flag
        // lives on the user profile (what the email fan-out actually reads), so
        // overlay it on top of the device record to keep both UIs in sync.
        let emailEnabled = data.emailEnabled ?? false;
        let email = data.email || '';
        let tok = null;
        try { tok = localStorage.getItem('token'); } catch (_) { /* noop */ }
        if (tok) {
          try {
            const ures = await fetch(`${API}/api/user/notification-preferences`, {
              headers: { Authorization: `Bearer ${tok}` },
            });
            if (ures.ok) {
              const udata = await ures.json();
              emailEnabled = udata.emailEnabled ?? emailEnabled;
            }
          } catch (_) { /* fall back to device value */ }
        }
        setPreferences({
          favouriteBusinessIds: data.favouriteBusinessIds || [],
          followedBusinessIds: data.followedBusinessIds || [],
          areaSubscriptions: data.areaSubscriptions || [],
          notificationsEnabled: data.notificationsEnabled ?? false,
          email,
          emailEnabled,
          featuredContentEnabled: data.featuredContentEnabled ?? false,
        });
      }
    } catch (e) {
      console.warn('[Notifications] Failed to load preferences:', e);
    }
  }, [subscriptionId]);

  // Iter 207 — one-time device merge. Historically a device could end up with
  // TWO device_subscriptions docs: one keyed by the OneSignal player id (where
  // follows/areas were written) and one keyed by the canonical find2day_sub_id
  // (where the bell/inbox/web-push read). That split silently dropped safety
  // alerts. This consolidates them into the canonical doc, re-keys past inbox
  // entries, and reloads prefs so the UI reflects the merged follows.
  useEffect(() => {
    try {
      const canonical = localStorage.getItem('find2day_sub_id');
      const legacy = localStorage.getItem('onesignal_sub_id');
      if (
        canonical && legacy && legacy !== canonical &&
        localStorage.getItem('f2d_device_merged_v1') !== '1'
      ) {
        fetch(`${API}/api/notifications/merge-device`, {
          method: 'POST',
          headers: _authHeaders(),
          body: JSON.stringify({ canonicalId: canonical, legacyId: legacy }),
        })
          .then(() => {
            localStorage.setItem('f2d_device_merged_v1', '1');
            loadPrefs();
            try { window.dispatchEvent(new Event('inbox:refresh')); } catch (_) { /* noop */ }
          })
          .catch(() => {});
      }
    } catch (_) { /* noop */ }
  }, [loadPrefs]);

  useEffect(() => {
    if (!subscriptionId) return;
    // Re-call /subscribe with the current auth header so the backend stamps
    // userId on this device sub. Critical when a user signs in AFTER they've
    // already received a push subscriptionId — without this, their userId is
    // never linked and the notification fan-out can't reach them.
    try {
      _postSubscribeDeduped(API, _authHeaders(), {
        subscriptionId,
        hasPush: !!readValidOneSignalId(),
        oneSignalId: readValidOneSignalId() || undefined,
      });
    } catch (_) { /* noop */ }
    loadPrefs();
  }, [subscriptionId, loadPrefs]);

  // Enable push notifications
  const enableNotifications = useCallback(async () => {
    if (!_sdkReady) return false;

    try {
      await OneSignal.Notifications.requestPermission();

      let subId = OneSignal.User.PushSubscription.id;
      let attempts = 0;
      while (!subId && attempts < 15) {
        await new Promise(r => setTimeout(r, 600));
        subId = OneSignal.User.PushSubscription.id;
        attempts++;
      }

      if (subId && isValidOneSignalId(subId)) {
        // Iter 207 — store the OneSignal player id on the canonical device doc
        // (find2day_sub_id); never switch the subscription key to it. Feb 2026
        // — only accept canonical UUIDs so we never persist a `local-…` shape.
        localStorage.setItem('onesignal_sub_id', subId);
        setIsSubscribed(true);

        await fetch(`${API}/api/notifications/subscribe`, {
          method: 'POST',
          headers: _authHeaders(),
          body: JSON.stringify({ subscriptionId: getLocalSubId(), hasPush: true, oneSignalId: subId }),
        });
        return true;
      }
      return false;
    } catch (e) {
      console.warn('[OneSignal] Enable failed:', e);
      return false;
    }
  }, []);

  // Iter 211 — when the user is logged in, the bell-modal email toggle must
  // ALSO write to their user-profile prefs (`users.notificationPreferences`),
  // because the backend email fan-out for Events/Offers/Safety reads THAT flag,
  // not the anonymous device_subscriptions record. Keeps both UIs in sync.
  const _syncUserEmailPref = useCallback(async (emailEnabled) => {
    let tok = null;
    try { tok = localStorage.getItem('token'); } catch (_) { /* noop */ }
    if (!tok) return; // anonymous users only have the device record
    // The user-profile gate requires BOTH emailEnabled AND per-category flags.
    // The bell modal only exposes a single master toggle, so when the user
    // turns email ON we also enable the content categories that map to the
    // modal's promise ("updates from businesses/towns you follow") — Events,
    // Offers, Lost & Found, Community and Critical Safety. System/marketing
    // updates stay OFF. This mirrors the single-flag device-subscription
    // behaviour so Events, Offers and Safety Alerts actually reach the user.
    const body = { emailEnabled: !!emailEnabled };
    if (emailEnabled) {
      body.categories = {
        events: true,
        businessOffers: true,
        lostAndFound: true,
        community: true,
        criticalSafety: true,
      };
    }
    try {
      await fetch(`${API}/api/user/notification-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify(body),
      });
    } catch (e) {
      console.warn('[Notifications] User-profile email pref sync failed:', e);
    }
  }, []);

  // One-tap opt-in used by the "Turn on email updates" follow prompt. Enables
  // the master email flag + content categories via the same fixed endpoint,
  // and reflects it locally so the /preferences page + bell UI stay in sync.
  const promoteEmailUpdates = useCallback(async () => {
    await _syncUserEmailPref(true);
    setPreferences(prev => ({
      ...prev,
      emailEnabled: true,
      categories: {
        ...(prev.categories || {}),
        events: true, businessOffers: true, lostAndFound: true,
        community: true, criticalSafety: true,
      },
    }));
    try { localStorage.setItem('f2d_email_prompt_ack', '1'); } catch (_) { /* noop */ }
  }, [_syncUserEmailPref]);

  // Gently prompt a logged-in follower to turn on email updates. Optional,
  // never shown by default, shown at most once until accepted/declined.
  const _maybePromptEmailUpdates = useCallback(() => {
    let tok = null;
    let acked = null;
    try {
      tok = localStorage.getItem('token');
      acked = localStorage.getItem('f2d_email_prompt_ack');
    } catch (_) { /* noop */ }
    if (!tok) return;                 // only for logged-in users
    if (acked === '1') return;        // already accepted or declined
    if (preferences.emailEnabled) return; // already opted in
    toast('Turn on email updates?', {
      id: 'email-follow-prompt',
      description: 'Get Events, Offers and Safety Alerts from businesses you follow by email. You can turn this off anytime.',
      duration: 12000,
      action: {
        label: 'Turn on',
        onClick: async () => {
          try {
            await promoteEmailUpdates();
            toast.success('Email updates are on. Manage them anytime in Notification settings.');
          } catch (_) {
            toast.error('Could not turn on email updates. Please try again.');
          }
        },
      },
      cancel: {
        label: 'No thanks',
        onClick: () => {
          try { localStorage.setItem('f2d_email_prompt_ack', '1'); } catch (_) { /* noop */ }
        },
      },
    });
  }, [preferences.emailEnabled, promoteEmailUpdates]);


  // Enable email-only subscription (for devices without push support)
  const enableEmailOnly = useCallback(async (email) => {
    const subId = getLocalSubId();
    try {
      // Register the device
      await fetch(`${API}/api/notifications/subscribe`, {
        method: 'POST',
        headers: _authHeaders(),
        body: JSON.stringify({ subscriptionId: subId, hasPush: false }),
      });
      // Set the email
      const res = await fetch(`${API}/api/notifications/update-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId: subId, email, emailEnabled: true }),
      });
      if (res.ok) {
        setSubscriptionId(subId);
        setIsSubscribed(true);
        setPreferences(prev => ({ ...prev, email, emailEnabled: true }));
        await _syncUserEmailPref(true);
        return true;
      }
      return false;
    } catch (e) {
      console.warn('[Notifications] Email-only subscribe failed:', e);
      return false;
    }
  }, [_syncUserEmailPref]);

  // Update fallback email (for users who already have push)
  const updateEmail = useCallback(async (email) => {
    if (!subscriptionId) return false;
    try {
      const res = await fetch(`${API}/api/notifications/update-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId, email, emailEnabled: !!email }),
      });
      if (res.ok) {
        const data = await res.json();
        setPreferences(prev => ({ ...prev, email: data.email, emailEnabled: data.emailEnabled }));
        await _syncUserEmailPref(data.emailEnabled);
        return true;
      }
      return false;
    } catch (e) {
      console.warn('[Notifications] Email update failed:', e);
      return false;
    }
  }, [subscriptionId, _syncUserEmailPref]);

  // Build a headers object that always carries Authorization when the user is
  // logged in. Critical for the backend to stamp `userId` on the device sub —
  // without it, notifications can't be fanned out to the user's account.
  const _authHeaders = () => {
    const h = { 'Content-Type': 'application/json' };
    try {
      const tok = localStorage.getItem('token');
      if (tok) h['Authorization'] = `Bearer ${tok}`;
    } catch (_) { /* noop */ }
    return h;
  };

  // Toggle favourite
  const toggleFavourite = useCallback(async (businessId) => {
    if (!subscriptionId) return null;
    try {
      const res = await fetch(`${API}/api/notifications/favourite`, {
        method: 'POST',
        headers: _authHeaders(),
        body: JSON.stringify({ subscriptionId, businessId }),
      });
      const data = await res.json();
      setPreferences(prev => ({
        ...prev,
        favouriteBusinessIds: data.favourited
          ? [...prev.favouriteBusinessIds, businessId]
          : prev.favouriteBusinessIds.filter(id => id !== businessId),
      }));
      // Force the bell to refresh so the badge reflects the new follow scope.
      try { window.dispatchEvent(new Event('inbox:refresh')); } catch (_) { /* noop */ }
      return data;
    } catch (e) {
      console.warn('[Notifications] Favourite toggle failed:', e);
      return null;
    }
  }, [subscriptionId]);

  // Toggle follow
  const toggleFollow = useCallback(async (businessId) => {
    if (!subscriptionId) return null;
    try {
      const res = await fetch(`${API}/api/notifications/follow`, {
        method: 'POST',
        headers: _authHeaders(),
        body: JSON.stringify({ subscriptionId, businessId }),
      });
      const data = await res.json();
      setPreferences(prev => ({
        ...prev,
        followedBusinessIds: data.following
          ? [...prev.followedBusinessIds, businessId]
          : prev.followedBusinessIds.filter(id => id !== businessId),
      }));
      try { window.dispatchEvent(new Event('inbox:refresh')); } catch (_) { /* noop */ }
      // On a NEW follow, gently offer to turn on email updates (Events, Offers,
      // Safety Alerts). Optional, off by default, one tap to accept or dismiss.
      if (data.following) _maybePromptEmailUpdates();
      return data;
    } catch (e) {
      console.warn('[Notifications] Follow toggle failed:', e);
      return null;
    }
  }, [subscriptionId, _maybePromptEmailUpdates]);

  // Make sure this device has at least one subscription record on the backend.
  // Used by "Follow town" flows so the user doesn't need to set up email first.
  // Tries push permission first (so push defaults ON when available). If push
  // fails or is unavailable, falls back to a local-only subscription so the
  // in-app inbox still fans out correctly for logged-in users.
  const ensureSubscription = useCallback(async () => {
    // Iter 207 — the canonical device key is ALWAYS find2day_sub_id. We try to
    // enable push (so push defaults ON when available) but never key the device
    // by the OneSignal id; that id is attached as a field via enableNotifications.
    const localId = getLocalSubId();
    if (_sdkReady) {
      try { await enableNotifications(); } catch (_) { /* fall through to local */ }
    }
    try {
      await fetch(`${API}/api/notifications/subscribe`, {
        method: 'POST',
        headers: _authHeaders(),
        body: JSON.stringify({
          subscriptionId: localId,
          hasPush: !!readValidOneSignalId(),
          oneSignalId: readValidOneSignalId() || undefined,
        }),
      });
      setSubscriptionId(localId);
      setIsSubscribed(true);
      return localId;
    } catch (e) {
      console.warn('[Notifications] ensureSubscription failed:', e);
      return localId;
    }
  }, [enableNotifications]);

  // Toggle area subscription
  const toggleArea = useCallback(async (area) => {
    // Auto-provision a subscription if the user doesn't have one yet.
    let subId = subscriptionId;
    if (!subId) subId = await ensureSubscription();
    if (!subId) return null;
    try {
      const res = await fetch(`${API}/api/notifications/area-subscribe`, {
        method: 'POST',
        headers: _authHeaders(),
        body: JSON.stringify({ subscriptionId: subId, area }),
      });
      const data = await res.json();
      setPreferences(prev => ({
        ...prev,
        areaSubscriptions: data.subscribed
          ? [...prev.areaSubscriptions, area]
          : prev.areaSubscriptions.filter(a => a !== area),
      }));
      return data;
    } catch (e) {
      console.warn('[Notifications] Area toggle failed:', e);
      return null;
    }
  }, [subscriptionId, ensureSubscription]);

  // Toggle featured/promoted content notifications
  const toggleFeaturedContent = useCallback(async () => {
    if (!subscriptionId) return null;
    try {
      const newVal = !preferences.featuredContentEnabled;
      const res = await fetch(`${API}/api/notifications/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId, featuredContentEnabled: newVal }),
      });
      if (res.ok) {
        setPreferences(prev => ({ ...prev, featuredContentEnabled: newVal }));
        return newVal;
      }
      return null;
    } catch (e) {
      console.warn('[Notifications] Featured content toggle failed:', e);
      return null;
    }
  }, [subscriptionId, preferences.featuredContentEnabled]);

  // ============= Self-hosted Web Push (VAPID) + Badging API =============
  // Listens for SET_BADGE messages from the service worker push handler so the
  // app-icon badge stays in sync when notifications arrive while the app is
  // backgrounded or closed (Android/desktop installed PWA + iOS 16.4+ PWA).
  const [webPushBadgeEnabled, setWebPushBadgeEnabled] = useState(false);
  const [webPushSupported] = useState(() => isWebPushSupported());
  const [badgingSupported] = useState(() => isBadgingSupported());

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event) => {
      const data = event && event.data;
      if (!data || data.type !== 'SET_BADGE') return;
      applyAppBadge(typeof data.count === 'number' ? data.count : 0);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  // Detect whether this device already has a saved Web Push subscription,
  // and (REG-009 auto-heal) silently re-register it under the canonical
  // find2day_sub_id so existing subscriptions keep delivering after the
  // ownerKey fix — no user tap and no permission prompt required.
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    (async () => {
      try {
        // The main '/' SW may not be retrievable at the exact instant this
        // effect runs on a fresh PWA launch. Previously we bailed the whole
        // effect on `!reg`, so auto-registration never ran and the device was
        // never added to `web_push_subscriptions` (badge could never update).
        // Fall back to serviceWorker.ready (resolves once a SW is active) and
        // only skip the has-subscription probe — never the registration below.
        const reg =
          (await navigator.serviceWorker.getRegistration('/')) ||
          (await navigator.serviceWorker.ready);
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          setWebPushBadgeEnabled(!!sub);
        }

        // Auto-enable the app-icon badge for any device that has ALREADY granted
        // notification permission (i.e. "phone alerts" are on) — no separate tap
        // and no new permission prompt. Registers a self-hosted VAPID push
        // subscription on our '/' SW (independent of OneSignal at '/onesignal/')
        // so the SW can call setAppBadge() when a notification arrives while the
        // app is backgrounded/closed. Previously this only RE-KEYED an existing
        // subscription, so brand-new devices never got one (0 subs in the DB) and
        // the badge could never update via push. Keyed per auth state so the
        // subscription is also stamped under user:<id> once the owner signs in.
        if (!isWebPushSupported()) return;
        const permGranted = typeof Notification === 'undefined' || Notification.permission === 'granted';
        const token = localStorage.getItem('token') || '';
        const regMarker = `f2d_webpush_reg_${token ? 'user' : 'anon'}`;
        if (permGranted && localStorage.getItem(regMarker) !== '1') {
          try {
            const subId = localStorage.getItem('find2day_sub_id') || (await ensureSubscription());
            if (subId) {
              await enableWebPushBadge(subId, token);
              setWebPushBadgeEnabled(true);
              localStorage.setItem(regMarker, '1');
            }
          } catch (_) { /* best-effort; AppBadgeToggle remains the manual fallback */ }
        }
      } catch (_) { /* noop */ }
    })();
    // ensureSubscription is defined above; intentional single-run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enableWebPush = useCallback(async () => {
    // Register the Web Push subscription under the CANONICAL device id
    // (find2day_sub_id) — the same ownerKey the safety-alert inbox fan-out
    // pushes to (`sub:<find2day_sub_id>`). Using the OneSignal id here would
    // register the badge subscription under a key the fan-out never targets,
    // so the app-icon badge would never update while the app is closed.
    const subId = (typeof localStorage !== 'undefined' && localStorage.getItem('find2day_sub_id')) || (await ensureSubscription());
    if (!subId) throw new Error('Could not create a device subscription.');
    const token = localStorage.getItem('token') || '';
    await enableWebPushBadge(subId, token);
    setWebPushBadgeEnabled(true);
    return true;
  // ensureSubscription is defined further up, ESLint is OK with this ordering
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId]);

  const disableWebPush = useCallback(async () => {
    const subId = subscriptionId || localStorage.getItem('find2day_sub_id') || '';
    if (!subId) return;
    await disableWebPushBadge(subId);
    setWebPushBadgeEnabled(false);
  }, [subscriptionId]);

  const testWebPush = useCallback(async () => {
    const subId = subscriptionId || localStorage.getItem('find2day_sub_id') || '';
    const token = localStorage.getItem('token') || '';
    return sendTestWebPush(subId, token);
  }, [subscriptionId]);

  return (
    <NotificationContext.Provider value={{
      initialized,
      sdkAvailable,
      isSubscribed,
      subscriptionId,
      preferences,
      initError,
      enableNotifications,
      enableEmailOnly,
      updateEmail,
      toggleFavourite,
      toggleFollow,
      toggleArea,
      toggleFeaturedContent,
      ensureSubscription,
      // Self-hosted Web Push (badge support)
      webPushSupported,
      badgingSupported,
      webPushBadgeEnabled,
      enableWebPush,
      disableWebPush,
      testWebPush,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
